import React from 'react';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent } from '../../../../../shared/index.js';
import Checkbox from '../../../Checkbox/Checkbox.js';
import Translate from '../../../Translate/Translate.js';
import ListItemCheckboxWrapper from '../../styledComponents/ListItemCheckboxWrapper.js';
import ListItemIcon from '../../styledComponents/ListItemIcon.js';
import ListItemIconWrapper from '../../styledComponents/ListItemIconWrapper.js';
import ListItemLabel from '../../styledComponents/ListItemLabel.js';
import ListItemLabelHighlight from '../../styledComponents/ListItemLabelHighlight.js';

import { IDropdownListItemConfiguration, IDropdownListItemProps, IDropdownListItemState } from './models/index.js';
import {
	DROPDOWN_LIST_ITEM_CATEGORY_KEY,
	DROPDOWN_LIST_ITEM_COMPONENT_KEY,
	DROPDOWN_LIST_ITEM_PARENT_COMPONENT_KEY,
} from './DropdownListItem.meta.js';

export class DropdownListItem extends ComponentBase<
	IDropdownListItemProps,
	IDropdownListItemState,
	IDropdownListItemConfiguration
> {
	categoryKey = DROPDOWN_LIST_ITEM_CATEGORY_KEY;
	componentKey = DROPDOWN_LIST_ITEM_COMPONENT_KEY;

	constructor(props: IDropdownListItemProps) {
		super(props);

		makeObservable(this, {
			listItemCheckBox: computed,
			listItemIcon: computed,
			itemLabel: computed,
		});
	}

	get parentComponentKey(): string | undefined {
		return DROPDOWN_LIST_ITEM_PARENT_COMPONENT_KEY;
	}

	componentDidMount() {
		this.setFocus();
	}

	componentDidUpdate() {
		this.setFocus();
	}

	setFocus() {
		const { isOpen, item } = this.props;
		if (isOpen && item && item.focused && item.ref) {
			item.ref.current.focus();
		}
	}

	get listItemCheckBox() {
		const { selected, onChange } = this.props;
		return (
			<ListItemCheckboxWrapper>
				<Checkbox value={selected} onChange={onChange} />
			</ListItemCheckboxWrapper>
		);
	}

	get listItemIcon() {
		const { iconProps } = this.props;
		return (
			<ListItemIconWrapper aria-hidden={'true'}>
				<ListItemIcon {...iconProps} />
			</ListItemIconWrapper>
		);
	}

	get itemLabel() {
		const { item } = this.props;
		const { label, highlight } = item;
		const labelElement = <Translate>{label}</Translate>;
		const listItemLabel = <ListItemLabel>{labelElement}</ListItemLabel>;
		const listItemLabelHighlight = <ListItemLabelHighlight>{labelElement}</ListItemLabelHighlight>;
		return highlight ? listItemLabelHighlight : listItemLabel;
	}

	render() {
		const {
			className,
			id,
			isMultiSelect,
			checkboxPosition = 'left',
			iconPosition,
			onChange,
			onKeyDown,
			selected,
			tabIndex,
			item,
		} = this.props;

		return (
			<li
				className={className}
				id={id}
				onClick={onChange}
				onKeyDown={onKeyDown}
				role={'option'}
				ref={item.ref}
				tabIndex={tabIndex}
				aria-selected={selected}
			>
				{isMultiSelect && checkboxPosition === 'left' && this.listItemCheckBox}
				{iconPosition === 'left' && this.listItemIcon}
				{this.itemLabel}
				{iconPosition === 'right' && this.listItemIcon}
				{isMultiSelect && checkboxPosition === 'right' && this.listItemCheckBox}
			</li>
		);
	}
}

export default InjectedStyledComponent(
	observer(DropdownListItem),
	DROPDOWN_LIST_ITEM_CATEGORY_KEY,
	DROPDOWN_LIST_ITEM_PARENT_COMPONENT_KEY,
	[DROPDOWN_LIST_ITEM_COMPONENT_KEY],
);
